import { useCallback, useEffect, useMemo, useState } from 'react'
// import { Upload } from 'lucide-react'
import { FacturaAPI } from '../../../../../../../api/facturas.api'
import { useGlobalContext } from '../../../../../../../context/GlobalState'
import { useBulkInvoice } from '../../../../../../../context/BulkInvoiceContext'
import LucaModal from '../../../../../ui/LucaModal'
import { Button } from '../../../../../ui'
import { excelRowsToDocuments } from '../../utils/excelRowsToDocuments'
import { mapEntityToReceiver } from '../../utils/mapEntityToReceiver'
import { mergeReceiverData } from '../../utils/mergeReceiverData'
import { sanitizeRut } from '../../utils/sanitizeRut'
import { getErrorMessage } from '../../utils/utils'
import { sileo } from 'sileo'
import FirmaEmisionMasiva from './FirmaEmisionMasiva'
import SubirExcel from './SubirExcel'
import StepperProceso from './StepperProceso'
import TablaCargasFacturas from './TablaCargasFacturas'
import VistaPreviaFacturas from './VistaPreviaFacturas'
import DetalleEmisionMasiva from './DetalleEmisionMasiva'
import ModalEditarFactura from './modalEdit/ModalEditarFactura'
import ProcesandoEmisionMasiva from './ProcesandoEmisionMasiva'
import ReporteEmisionMasiva from './ReporteEmisionMasiva'
const calculateDocumentTotals = doc => {
  const subtotal = (doc.details || []).reduce((acc, item) => {
    const price = Number(item.unit_price) || 0
    const qty = Number(item.quantity) || 0
    const discount = Number(item.discount_percent) || 0
    return acc + price * qty * (1 - discount / 100)
  }, 0)

  const isExenta = String(doc?.dte_type?.code || '') === '34'
  const iva = isExenta ? 0 : subtotal * 0.19
  const total = subtotal + iva

  return { subtotal, iva, total }
}

const normalizePersistedDocument = (detail, fallbackDate) => {
  const persistedDocument = detail?.payload_enviado?.documents?.[0]

  if (!persistedDocument) {
    return {
      dte_type: { code: String(detail?.tipo_dte || '33') },
      document_receiver: {
        rut: detail?.receptor_rut || '',
        business_name: detail?.receptor_nombre || ''
      },
      details: [],
      date_issued: fallbackDate,
      estado_final: detail?.estado_final,
      motivo_resultado: detail?.motivo_resultado,
      documento_emitido: detail?.documento_emitido
    }
  }

  return {
    ...persistedDocument,
    date_issued: persistedDocument?.date_issued || fallbackDate,
    document_receiver: {
      ...(persistedDocument?.document_receiver || {}),
      email:
        persistedDocument?.document_receiver?.email ||
        persistedDocument?.document_receiver?.contact ||
        ''
    },
    estado_final: detail?.estado_final,
    motivo_resultado: detail?.motivo_resultado,
    documento_emitido: detail?.documento_emitido
  }
}

const mapPersistedUploadToView = persistedUpload => {
  const payload = persistedUpload?.payloadJson || {}
  const details = Array.isArray(payload?.detalle_emision) ? payload.detalle_emision : []
  const fallbackDate = payload?.fecha_reporte ? String(payload.fecha_reporte).slice(0, 10) : ''

  return {
    documents: details.map(detail => normalizePersistedDocument(detail, fallbackDate)),
    uploadReport: {
      failed: Number(payload?.documentos_fallidos) || 0,
      processed: Number(payload?.total_documentos) || details.length,
      errorDetails: details
        .filter(detail => detail?.estado_final !== 'success')
        .map(detail => ({
          row: detail?.fila || '-',
          receiver: detail?.receptor_nombre || detail?.receptor_rut || 'Sin receptor',
          errors: [detail?.motivo_resultado || 'Error al procesar documento']
        }))
    },
    fileName: payload?.nombre_archivo || payload?.fileName || `Carga #${persistedUpload?.id || ''}`
  }
}

export default function FacturasMasivas({ onEmitSuccess }) {
  const facturaApi = useMemo(() => new FacturaAPI(), [])
  const { currentBusiness } = useGlobalContext()

  const [activeStep, setActiveStep] = useState(0)
  const [documents, setDocuments] = useState([])
  const [uploadReport, setUploadReport] = useState(null)
  const [excelFileName, setExcelFileName] = useState('')
  const [isLoadingRut, setIsLoadingRut] = useState(false)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [bulkUploadReports, setBulkUploadReports] = useState([])
  const [isLoadingBulkUploadReports, setIsLoadingBulkUploadReports] = useState(false)
  const [selectedBulkUploadReport, setSelectedBulkUploadReport] = useState(null)
  const [emissionResults, setEmissionResults] = useState([])

  const {
    isProcessing,
    isFinished,
    resetEmission,
    documents: globalDocs,
    uploadReport: globalReport,
    startEmission
  } = useBulkInvoice()

  // Sincronizar estado local con global al montar el componente
  useEffect(() => {
    if (globalDocs.length > 0) {
      setDocuments(globalDocs)
    }
    if (globalReport) {
      setUploadReport(globalReport)
      setExcelFileName(globalReport.fileName || '')
    }
  }, [globalDocs, globalReport])

  const fetchBulkUploadReports = useCallback(async () => {
    if (!currentBusiness?.id) {
      setBulkUploadReports([])
      return
    }

    setIsLoadingBulkUploadReports(true)

    try {
      const response = await facturaApi.getBulkInvoiceUploads(currentBusiness.id)
      const reports = Array.isArray(response?.data) ? response.data : []
      setBulkUploadReports(reports)
    } catch (error) {
      setBulkUploadReports([])
      sileo.error({
        title: 'Error al cargar informes',
        description: getErrorMessage(
          error,
          'No se pudieron cargar las cargas masivas registradas.'
        )
      })
    } finally {
      setIsLoadingBulkUploadReports(false)
    }
  }, [currentBusiness?.id, facturaApi])

  // Cargar reportes al montar y cuando cambia la empresa
  useEffect(() => {
    fetchBulkUploadReports()
  }, [fetchBulkUploadReports])

  const selectedBulkUploadView = selectedBulkUploadReport
    ? mapPersistedUploadToView(selectedBulkUploadReport)
    : null

  useEffect(() => {
    if (documents.length === 0 && !uploadReport?.processed && activeStep !== 0) {
      setActiveStep(0)
    }
  }, [documents.length, uploadReport?.processed, activeStep])

  const handleOpenUploadModal = () => {
    if (isProcessing) {
      setActiveStep(2)
      setIsUploadModalOpen(true)
      return
    }
    if (isFinished) {
      handleClearReport()
      setIsUploadModalOpen(true)
      return
    }
    setActiveStep(0)
    setIsUploadModalOpen(true)
  }

  const handleResumeFlowModal = () => {
    if (isProcessing) {
      setActiveStep(2)
      setIsUploadModalOpen(true)
      return
    }
    if (documents.length === 0 && !uploadReport?.processed) return
    setIsUploadModalOpen(true)
  }

  const handleCloseUploadModal = () => {
    if (isLoadingRut) return
    setIsUploadModalOpen(false)
  }

  const handleMinimize = () => {
    setIsUploadModalOpen(false)
  }

  const handleOpenReportModal = row => {
    if (row?.source === 'remote' && row?.rawReport) {
      setSelectedBulkUploadReport(row.rawReport)
      setIsReportModalOpen(true)
      return
    }

    if (documents.length === 0 && !uploadReport?.processed) return

    setSelectedBulkUploadReport(null)
    setIsReportModalOpen(true)
  }

  const handleCloseReportModal = () => {
    setSelectedBulkUploadReport(null)
    setIsReportModalOpen(false)
  }

  const handleDelete = index => {
    setDocuments(prev => prev.filter((_, currentIndex) => currentIndex !== index))
  }

  const handleEdit = index => {
    setSelectedIndex(index)
    setIsEditModalOpen(true)
  }

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false)
    setSelectedIndex(null)
  }

  const handleSaveModal = updatedDoc => {
    if (selectedIndex == null) return

    setDocuments(prev => prev.map((doc, index) => (index === selectedIndex ? updatedDoc : doc)))
    handleCloseEditModal()
  }

  const handleClearReport = () => {
    setDocuments([])
    setUploadReport(null)
    setEmissionResults([])
    setExcelFileName('')
    setActiveStep(0)
    setIsUploadModalOpen(false)
    setIsReportModalOpen(false)
    setSelectedBulkUploadReport(null)
    handleCloseEditModal()

    if (isFinished) {
      resetEmission()
    }
  }

  const onUploadExcel = async ({ json, fileName }) => {
    if (isProcessing) {
      sileo.warning({
        title: 'Proceso en curso',
        description: 'Ya existe una carga masiva procesándose. Por favor espera a que termine.'
      })
      return
    }

    setExcelFileName(fileName)
    setIsLoadingRut(true)

    try {
      const { documents: parsedDocs, report } = excelRowsToDocuments(json)
      const uniqueRuts = Array.from(
        new Set(parsedDocs.map(doc => sanitizeRut(doc.document_receiver?.rut)).filter(Boolean))
      )

      const entityEntries = await Promise.all(
        uniqueRuts.map(async rut => {
          const cleanRut = sanitizeRut(rut)

          try {
            const response = await facturaApi.getSearchEntityRut(cleanRut)
            const entity = response?.data || response
            return [cleanRut, mapEntityToReceiver(entity)]
          } catch {
            return [cleanRut, null]
          }
        })
      )

      const receiverCache = Object.fromEntries(entityEntries)
      const mergedDocs = parsedDocs.map(doc => {
        const rut = sanitizeRut(doc.document_receiver?.rut)
        const receiverFromApi = receiverCache[rut]

        if (!receiverFromApi) return doc

        return {
          ...doc,
          document_receiver: mergeReceiverData(receiverFromApi, doc.document_receiver)
        }
      })

      setDocuments(mergedDocs)
      setUploadReport({
        ...report,
        createdAt: new Date().toISOString(),
        fileName
      })
      setIsUploadModalOpen(true)
      setActiveStep(1)

    } finally {
      setIsLoadingRut(false)
    }
  }

  const handleEmitMassive = async () => {
    if (!currentBusiness?.id || documents.length === 0) return
    setActiveStep(2)
  }

  const handleFinishEmission = results => {
    setEmissionResults(results)
    setActiveStep(3)
    // Re-fetch para que la tabla refleje el nuevo reporte sin necesidad de refresh
    fetchBulkUploadReports()
  }

  return (
    <div className='py-6'>
      <TablaCargasFacturas
        reports={bulkUploadReports}
        documents={documents}
        uploadReport={uploadReport}
        isProcessing={isProcessing}
        isLoading={isLoadingRut || isLoadingBulkUploadReports}
        onViewReport={handleOpenReportModal}
        handleOpenUploadModal={handleOpenUploadModal}
        handleResumeFlowModal={handleResumeFlowModal}
      />

      <LucaModal
        open={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        maxWidth='max-w-6xl'
        footer={
          activeStep === 1 ? (
            <>
              <Button
                onClick={() => setActiveStep(0)}
                className='text-[10px] bg-primary text-white rounded-full'
                title='Atrás'
              />
              {documents.length === 0 && (
                <Button
                  onClick={handleCloseUploadModal}
                  className='text-[10px] bg-primary text-white rounded-full'
                  title='Cerrar'
                />
              )}
            </>
          ) : activeStep === 0 ? (
            <Button
              onClick={handleCloseUploadModal}
              className='bg-primary text-white rounded-full'
              title='Cerrar'
            />
          ) : activeStep === 3 ? (
            <Button
              onClick={() => {
                handleClearReport()
                if (onEmitSuccess) onEmitSuccess(false)
              }}
              className='text-[10px] bg-primary text-white rounded-full'
              title='Cerrar'
            />
          ) : null
        }
      >
        <div className='flex flex-col h-full overflow-hidden rounded-2xl bg-white'>
          <div className='border-b border-slate-100'>
            <StepperProceso activeStep={activeStep} />
          </div>

          <div className='flex-1 overflow-hidden flex flex-col'>
            {activeStep === 0 && (
              <SubirExcel onUploadSuccess={onUploadExcel} isLoadingRut={isLoadingRut} />
            )}

            {activeStep === 1 && (
              <div className='flex-1 min-h-0 flex flex-col pt-4 pl-4 pr-4 pb-0 gap-3'>
                <div className='flex-1 min-h-0 flex flex-col'>
                  <VistaPreviaFacturas
                    currentBusiness={currentBusiness}
                    documents={documents}
                    uploadReport={uploadReport}
                    fileName={excelFileName}
                    isLoading={isLoadingRut}
                    calculateTotals={calculateDocumentTotals}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                </div>

                {/* Caja de Firma integrada en el paso de previsualización */}
                {uploadReport?.failed === 0 && documents.length > 0 && (
                  <div className='flex-shrink-0'>
                    <FirmaEmisionMasiva onEmit={handleEmitMassive} />
                  </div>
                )}
              </div>
            )}

            {activeStep === 2 && (
              <div className='min-h-0 flex flex-col overflow-y-auto bg-slate-50'>
                <ProcesandoEmisionMasiva
                  documents={documents}
                  currentBusiness={currentBusiness}
                  onFinish={handleFinishEmission}
                  onMinimize={handleMinimize}
                  startEmission={() => startEmission(documents, currentBusiness, uploadReport)}
                />
              </div>
            )}

            {activeStep === 3 && (
              <div className='flex-1 min-h-0 flex flex-col'>
                <ReporteEmisionMasiva results={emissionResults} />
              </div>
            )}
          </div>
        </div>
      </LucaModal>

      <LucaModal
        open={isReportModalOpen}
        onClose={handleCloseReportModal}
        maxWidth='max-w-7xl'
        footer={
          <Button
            onClick={handleCloseReportModal}
            className='text-[10px] bg-primary text-white rounded-full'
            title='Cerrar'
          />
        }
      >
        <div className='flex flex-col flex-1 min-h-0 overflow-hidden p-6 max-h-[620px]'>
          {selectedBulkUploadView ? (
            <DetalleEmisionMasiva
              currentBusiness={currentBusiness}
              documents={selectedBulkUploadView.documents}
              isLoading={isLoadingBulkUploadReports}
              calculateTotals={calculateDocumentTotals}
            />
          ) : (
            <VistaPreviaFacturas
              currentBusiness={currentBusiness}
              documents={documents}
              isLoading={isLoadingRut}
              calculateTotals={calculateDocumentTotals}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          )}
        </div>
      </LucaModal>

      <ModalEditarFactura
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        documento={selectedIndex != null ? documents[selectedIndex] : null}
        onSave={handleSaveModal}
      />
    </div>
  )
}
